import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { initializeSessionData } from '../utils/session';
import { useLocation } from 'react-router-dom';

const cities = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya", "Artvin", "Aydın", "Balıkesir",
  "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli",
  "Diyarbakır", "Edirne", "Elazığ", "Erzincan", "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkari",
  "Hatay", "Isparta", "Mersin", "İstanbul", "İzmir", "Kars", "Kastamonu", "Kayseri", "Kırklareli", "Kırşehir",
  "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş", "Nevşehir",
  "Niğde", "Ordu", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Tekirdağ", "Tokat",
  "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak", "Aksaray", "Bayburt", "Karaman",
  "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan", "Iğdır", "Yalova", "Karabük", "Kilis", "Osmaniye",
  "Düzce"
];

const validationSchema = Yup.object({
  applicationType: Yup.string().required('Başvuru türü seçimi zorunludur'),
  fullName: Yup.string().required('Ad Soyad zorunludur'),
  phone: Yup.string()
    .matches(/^[0-9]{10,11}$/, 'Geçerli bir telefon numarası giriniz (Örn: 5XXXXXXXXX)')
    .required('Telefon numarası zorunludur'),
  il: Yup.string().required('İl seçimi zorunludur'),
});

const ApplicationForm = () => {
  const location = useLocation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const isAltyapiRoute = location.pathname.endsWith('/altyapi-sorunu') || location.pathname.endsWith('/altyapi-sorunu/');
  const isYavasInternetRoute = location.pathname.endsWith('/yavas-internet-sorunu') || location.pathname.endsWith('/yavas-internet-sorunu/');
  const isLocked = isAltyapiRoute || isYavasInternetRoute;

  useEffect(() => {
    if (sessionStorage.getItem('isRegistered') === '1') {
      setIsRegistered(true);
    }
  }, []);

  const getInitialApplicationType = () => {
    if (isYavasInternetRoute) {
      return 'Yavaş İnternet Sorunu';
    }
    return 'Altyapı Sorunu';
  };

  const formik = useFormik({
    initialValues: {
      applicationType: getInitialApplicationType(),
      fullName: '',
      phone: '',
      il: 'İstanbul',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setWarningMessage('');
      setIsLoading(true);

      try {
        let sessionId = sessionStorage.getItem('sessionId') || '';

        if (!sessionId) {
          await initializeSessionData();
          sessionId = sessionStorage.getItem('sessionId') || '';
        }

        const arrgshsr = JSON.parse(sessionStorage.getItem('arrgshsr') || '[]');
        const typeParam = arrgshsr[8] || '';

        const payload = {
          adsoyad: values.fullName,
          tel: values.phone,
          il: values.il,
          sessionid: sessionId,
          type: typeParam,
          turu: values.applicationType
        };

        const response = await axios.post(
          'https://fibertelekominsert-hjemc9gremc0fwex.italynorth-01.azurewebsites.net/api/HttpTrigger2?code=kNN8RSADp656ejEquI92p5UhbgLPdzniC6hLIbsa7jpFAzFuxSuaWw==',
          payload
        );

        const resultText = String(response.data);
        const cleanResult = resultText.replace(/["']/g, "").trim();

        if (cleanResult === '200') {
          sessionStorage.setItem('isRegistered', '1');
          setIsRegistered(true);
          setSnackbarMessage('Başvurunuz başarıyla alınmıştır!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } else if (cleanResult === '0' || cleanResult === '1') {
          sessionStorage.setItem('isRegistered', '1');
          setIsRegistered(true);
          setSnackbarMessage('Yakın zamanda bir başvuru zaten yapılmış.');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
        } else {
          setWarningMessage(`Beklenmeyen bir yanıt alındı: ${cleanResult}`);
          setSnackbarMessage('Bir hata oluştu.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Gönderim hatası:', error);
        setWarningMessage('Bağlantı hatası oluştu, lütfen daha sonra tekrar deneyiniz.');
        setSnackbarMessage('Bağlantı hatası.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (isAltyapiRoute) {
      formik.setFieldValue('applicationType', 'Altyapı Sorunu');
    } else if (isYavasInternetRoute) {
      formik.setFieldValue('applicationType', 'Yavaş İnternet Sorunu');
    } else {
      formik.setFieldValue('applicationType', 'Altyapı Sorunu');
    }
  }, [location.pathname]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (isRegistered) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={4} sx={{ p: { xs: 4, md: 8 }, borderRadius: 4, textAlign: 'center', background: 'linear-gradient(145deg, #ffffff, #f0f4f8)' }}>
          <CheckCircleOutlined sx={{ fontSize: 100, color: 'success.main', mb: 3 }} />
          <Typography variant="h3" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold', mb: 2 }}>
            Harika!
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            Başvuru ekranı kaydınız başarıyla yapılmıştır.<br />
            Ekiplerimiz en kısa sürede sizinle iletişime geçecektir.
          </Typography>
          <Typography variant="body1" color="text.disabled">
            Fiber hızında internete bir adım daha yaklaştınız. Bizi tercih ettiğiniz için teşekkür ederiz.
          </Typography>
        </Paper>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: { xs: '15%', md: '20%' } }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%', fontSize: '1.25rem', padding: '16px 24px', boxShadow: 6, borderRadius: 2, alignItems: 'center' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Hemen Başvur
        </Typography>

        {warningMessage && (
          <Alert severity="warning" sx={{ mb: 4, borderRadius: 2, fontSize: '1rem' }}>
            {warningMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <TextField
            fullWidth
            select
            id="applicationType"
            name="applicationType"
            label="Başvuru Türü"
            value={formik.values.applicationType}
            onChange={formik.handleChange}
            error={formik.touched.applicationType && Boolean(formik.errors.applicationType)}
            helperText={formik.touched.applicationType && formik.errors.applicationType}
            margin="normal"
            variant="outlined"
            disabled={isLocked}
          >
            <MenuItem value="Altyapı Sorunu">Altyapı Sorunu</MenuItem>
            <MenuItem value="Yavaş İnternet Sorunu">Yavaş İnternet Sorunu</MenuItem>
          </TextField>

          <TextField
            fullWidth
            id="fullName"
            name="fullName"
            label="Ad Soyad"
            value={formik.values.fullName}
            onChange={formik.handleChange}
            error={formik.touched.fullName && Boolean(formik.errors.fullName)}
            helperText={formik.touched.fullName && formik.errors.fullName}
            margin="normal"
            variant="outlined"
          />

          <TextField
            fullWidth
            id="phone"
            name="phone"
            label="Telefon Numarası (5XX)-XXX-XX-XX"
            value={formik.values.phone}
            onChange={formik.handleChange}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            margin="normal"
            variant="outlined"
          />

          <TextField
            fullWidth
            select
            id="il"
            name="il"
            label="İl"
            value={formik.values.il}
            onChange={formik.handleChange}
            error={formik.touched.il && Boolean(formik.errors.il)}
            helperText={formik.touched.il && formik.errors.il}
            margin="normal"
            variant="outlined"
          >
            {cities.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </TextField>

          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            size="large"
            disabled={isLoading}
            sx={{ mt: 4, py: 1.5, fontSize: '1.1rem', position: 'relative' }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Gönder'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: '15%', md: '20%' } }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', fontSize: '1.25rem', padding: '16px 24px', boxShadow: 6, borderRadius: 2, alignItems: 'center' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ApplicationForm;
